import { useState } from 'react'
import { unitId, actionId } from '@/annotations'
import { WizardSplit, WizardCard, WizardHeading, WizardProse, WizardButtons } from '@/components/WizardParts'
import { Field } from '@/components/Field'
import { Button } from '@/components/Button'
import { Note } from '@/components/Note'
import { Icon } from '@/components/Icon'
import { OB_WZ_2_COPY } from '@/copy/onboarding'
import { TEXT_INPUT_CLASSNAME, TEXT_INPUT_STYLE } from '@/styles/formControls'

/** `ob-wz-2` — stage 2's second pane. Collects the workspace slug; validated together with the
 * credential when stage 3 submits `workspaceConnect` (see `state.ts`'s header comment).
 *
 * **`data-cmp="pane"` and the two buttons' own icons added W156** — the spec's own Components
 * table named `icon` "in `button` — 2×", one per `WizardButtons` control.
 *
 * **Forward button's `data-act` corrected from `nav.ob-wz-3` to `workspace.connect`** — same fix,
 * same reason, as `ObWz1.tsx`'s own doc comment: rule B1 names this pane's own control as the
 * `workspaceConnect` operation's, not a plain navigation, even though the actual call still only
 * fires once from `ob-wz-3` (§12.4). The `Back` button stays `nav.ob-wz-1` — the spec's Actions
 * table names only the forward control.
 *
 * **`initialWorkspaceName` added** — `state.ts`'s own `BACK` case now carries the slug backward
 * from stage 3, so a return trip here seeds the field instead of remounting it blank. */
export function ObWz2({ initialWorkspaceName, onBack, onNext }: { initialWorkspaceName?: string; onBack: () => void; onNext: (workspaceName: string) => void }) {
  const [slug, setSlug] = useState(initialWorkspaceName ?? '')

  return (
    <div data-scr={unitId('ob-wz-2')} data-cmp="pane" className="p-10">
      <WizardSplit>
        <div>
          <WizardHeading>{OB_WZ_2_COPY.heading}</WizardHeading>
          <WizardProse>{OB_WZ_2_COPY.proseOne}</WizardProse>
          <WizardProse>{OB_WZ_2_COPY.proseTwo}</WizardProse>
        </div>
        <WizardCard>
          <h4 className="m-0 mb-7 text-body-lg font-semibold">{OB_WZ_2_COPY.cardHeading}</h4>
          <Field label={OB_WZ_2_COPY.slugLabel}>
            <input className={TEXT_INPUT_CLASSNAME} style={TEXT_INPUT_STYLE} value={slug} onChange={(e) => setSlug(e.target.value)} />
          </Field>
          <Note>{OB_WZ_2_COPY.slugNote}</Note>
          <div className="mt-4">
            <Note>{OB_WZ_2_COPY.adminWarning}</Note>
          </div>
          <WizardButtons>
            <Button data-act={actionId('nav.ob-wz-1')} ghost onClick={onBack}>
              <Icon name="i-chevL" size="s" />
              {OB_WZ_2_COPY.backButton}
            </Button>
            <Button data-act={actionId('workspace.connect')} disabled={!slug} onClick={() => onNext(slug)}>
              {OB_WZ_2_COPY.nextButton}
              <Icon name="i-chevR" size="s" />
            </Button>
          </WizardButtons>
        </WizardCard>
      </WizardSplit>
    </div>
  )
}
